import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Download, RefreshCw } from 'lucide-react';

interface AppUpdateDialogProps {
  /** Controls visibility of the dialog */
  open: boolean;
  /**
   * When true, renders a blocking modal (no dismiss option).
   * When false, renders a dismissible suggestion.
   */
  mandatory: boolean;
  /** Called when user taps "Atualizar Agora" */
  onUpdate: () => void;
  /** Called when user taps "Agora Não" (only available when mandatory=false) */
  onDismiss: () => void;
}

/**
 * Displays a dialog prompting the user to update the app.
 *
 * - mandatory=true: blocking modal, no dismiss, only "Atualizar Agora"
 * - mandatory=false: dismissible modal with "Atualizar Agora" and "Agora Não"
 *
 * The backdrop click and Escape key are disabled when mandatory=true
 * to prevent accidental dismissal.
 */
export function AppUpdateDialog({
  open,
  mandatory,
  onUpdate,
  onDismiss,
}: AppUpdateDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={mandatory ? undefined : (v) => !v && onDismiss()}>
      <AlertDialogContent className="mx-4 rounded-2xl border-0 shadow-2xl">

        <AlertDialogHeader className="items-center text-center gap-3 pt-2">
          {/* Icon */}
          <div
            className={`flex h-14 w-14 items-center justify-center rounded-full ${
              mandatory
                ? 'bg-destructive/10 text-destructive'
                : 'bg-primary/10 text-primary'
            }`}
          >
            {mandatory ? (
              <RefreshCw className="h-7 w-7" />
            ) : (
              <Download className="h-7 w-7" />
            )}
          </div>

          <AlertDialogTitle className="text-xl font-semibold">
            {mandatory ? 'Atualização Obrigatória' : 'Nova Versão Disponível'}
          </AlertDialogTitle>

          <AlertDialogDescription className="text-sm text-muted-foreground text-center leading-relaxed">
            {mandatory
              ? 'Esta versão do app não é mais suportada. Por favor, atualize para continuar usando o CareOS.'
              : 'Uma nova versão do CareOS está disponível com melhorias e correções. Recomendamos atualizar.'}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="flex-col gap-2 sm:flex-col mt-2">
          <AlertDialogAction
            onClick={onUpdate}
            className="w-full h-11 font-medium"
          >
            Atualizar Agora
          </AlertDialogAction>

          {!mandatory && (
            <AlertDialogCancel
              onClick={onDismiss}
              className="w-full h-11 font-medium border-0 bg-muted/50 hover:bg-muted mt-0"
            >
              Agora Não
            </AlertDialogCancel>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
